import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconPlus,
  IconBolt,
  IconRefresh,
  IconSearch,
  IconLogout,
  IconLoader2,
  IconAlertCircle,
  IconTrendingUp,
  IconServer,
  IconBan,
  IconActivity,
  IconCrown
} from '@tabler/icons-react';

import TunnelCard from '../components/dashboard/TunnelCard';
import CreateTunnelModal from '../components/dashboard/CreateTunnelModal';
import AddEmailBanner from '../components/dashboard/AddEmailBanner';
import CombinedAnalyticsTab from '../components/analytics/CombinedAnalyticsTab';
import { useAuth } from '../contexts/AuthContext';
import { useTunnels } from '../hooks/useTunnels';

const DashboardPage = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('servers');

  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const {
    tunnels,
    loading,
    error,
    fetchTunnels,
    createTunnel,
    deleteTunnel
  } = useTunnels({ refreshInterval: 5000 });
  
  const navigate = useNavigate();

  // Redirect to login only after auth check is complete
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const activeTunnels = tunnels.filter(t => t.status === 'active').length;
  // Получаем maxTunnels из user (из API /api/auth/me)
  const maxTunnels = user?.max_tunnels || 10;

  const filteredTunnels = tunnels.filter(tunnel => {
    return (tunnel.name && tunnel.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
           (tunnel.publicAddress && tunnel.publicAddress.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  const handleDelete = async (id) => {
    if (window.confirm('Удалить этот туннель?')) {
      try {
        await deleteTunnel(id);
      } catch (err) {
        alert('Ошибка: ' + err.message);
      }
    }
  };

  const handleCreate = async (tunnelData) => {
    try {
      await createTunnel(tunnelData);
      setShowCreateModal(false);
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950 pt-24 pb-16 transition-colors">
      <div className="max-w-[1200px] mx-auto px-6">
        
        {/* Alert Bar: призыв привязать email к аккаунту */}
        {user && !user.email && <AddEmailBanner />}

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="text-[28px] sm:text-[32px] lg:text-[40px] font-bold text-gray-900 dark:text-white mb-2 tracking-[-0.5px]">
              Панель управления
            </h1>
            <p className="text-gray-600 dark:text-dark-500">
              {user?.username && `${user.username} • `}
              Управление туннелями
            </p>
          </div>
          
          <div className="mt-4 lg:mt-0 flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex-1 sm:flex-none px-4 sm:px-5 py-2.5 bg-primary-500 hover:bg-primary-600 rounded-lg font-medium text-white flex items-center justify-center gap-2 transition-colors"
            >
              <IconPlus className="w-5 h-5" />
              <span className="sm:inline">Создать туннель</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-800 mb-6">
          <div className="flex gap-8 px-6">
            <button
              onClick={() => setActiveTab('servers')}
              className={`py-4 font-medium transition-colors relative ${
                activeTab === 'servers'
                  ? 'text-primary-500'
                  : 'text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Туннели
              {activeTab === 'servers' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500"></div>
              )}
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 font-medium transition-colors relative ${
                activeTab === 'analytics'
                  ? 'text-primary-500'
                  : 'text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Аналитика
              {activeTab === 'analytics' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500"></div>
              )}
            </button>
          </div>
        </div>

        {/* Content by Tab */}
        {activeTab === 'servers' && (
          <>
        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-xl mb-6">
            <IconAlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400">{error}</p>
            <button 
              onClick={fetchTunnels}
              className="ml-auto text-red-400 hover:text-red-300 underline"
            >
              Повторить
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Active Tunnels */}
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 hover:border-primary-500/30 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-500/10 rounded-xl flex items-center justify-center">
                <IconBolt className="w-6 h-6 text-primary-500" />
              </div>
              <div>
                <p className="text-gray-600 dark:text-dark-500 text-sm">Активные</p>
                <p className="text-gray-900 dark:text-white text-2xl font-bold">{activeTunnels}/{tunnels.length}</p>
              </div>
            </div>
          </div>

          {/* Total Tunnels */}
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 hover:border-primary-500/30 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-500/10 rounded-xl flex items-center justify-center">
                <IconServer className="w-6 h-6 text-primary-500" />
              </div>
              <div>
                <p className="text-gray-600 dark:text-dark-500 text-sm">Всего туннелей</p>
                <p className="text-gray-900 dark:text-white text-2xl font-bold">{tunnels.length}/{maxTunnels}</p>
              </div>
            </div>
          </div>

          {/* Blocked Tunnels */}
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 hover:border-primary-500/30 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-500/10 rounded-xl flex items-center justify-center">
                <IconBan className="w-6 h-6 text-primary-500" />
              </div>
              <div>
                <p className="text-gray-600 dark:text-dark-500 text-sm">Заблокировано</p>
                <p className="text-gray-900 dark:text-white text-2xl font-bold">{tunnels.filter(t => t.is_blocked).length}</p>
              </div>
            </div>
          </div>

          {/* Plan */}
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 hover:border-primary-500/30 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-500/10 rounded-xl flex items-center justify-center">
                {user?.plan === 'paid' ? (
                  <IconCrown className="w-6 h-6 text-primary-500" />
                ) : (
                  <IconBolt className="w-6 h-6 text-primary-500" />
                )}
              </div>
              <div>
                <p className="text-gray-600 dark:text-dark-500 text-sm">Тариф</p>
                <div className="flex items-center gap-2">
                  <p className="text-gray-900 dark:text-white text-2xl font-bold">{user?.plan === 'paid' ? 'Pro' : 'Free'}</p>
                  {user?.plan === 'paid' && user?.plan_expires_at && (
                    <span className="text-gray-600 dark:text-dark-500 text-xs bg-gray-100 dark:bg-dark-800 px-2 py-0.5 rounded">
                      до {new Date(user.plan_expires_at).toLocaleDateString('ru-RU')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* IconSearch */}
        <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <IconSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
              <input
                type="text"
                placeholder="Поиск туннелей..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-dark-950 border border-gray-200 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
              />
            </div>
            <button 
              onClick={fetchTunnels}
              disabled={loading}
              className="p-3 bg-gray-50 dark:bg-dark-950 border border-gray-200 dark:border-dark-800 rounded-lg text-gray-500 dark:text-dark-500 hover:text-primary-500 hover:border-primary-500/30 transition-colors disabled:opacity-50"
            >
              <IconRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && tunnels.length === 0 && (
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-16 text-center">
            <IconLoader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-dark-500">Загрузка туннелей...</p>
          </div>
        )}

        {/* Tunnels List */}
        {!loading && filteredTunnels.length > 0 && (
          <div className="space-y-3">
            {filteredTunnels.map(tunnel => (
              <TunnelCard
                key={tunnel.id}
                tunnel={tunnel}
                onDelete={() => handleDelete(tunnel.id)}
                onUpdateSettings={() => fetchTunnels()}
                onRefresh={fetchTunnels}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredTunnels.length === 0 && (
          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-16 text-center">
            <div className="w-16 h-16 mx-auto mb-5 bg-gradient-to-br from-primary-500 to-yellow-400 rounded-2xl flex items-center justify-center">
              <IconActivity className="w-8 h-8 text-black" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {searchQuery ? 'Ничего не найдено' : 'Нет туннелей'}
            </h3>
            <p className="text-gray-600 dark:text-dark-500 mb-6">
              {searchQuery 
                ? 'Попробуйте изменить запрос'
                : 'Создайте первый туннель'
              }
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-medium text-white inline-flex items-center gap-2 transition-colors"
              >
                <IconPlus className="w-5 h-5" />
                <span>Создать туннель</span>
              </button>
            )}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <CreateTunnelModal
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreate}
          />
        )}
        </>
        )}

        {activeTab === 'analytics' && (
          <CombinedAnalyticsTab />
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
